import { usePageTitle } from "@/hooks/use-page-title";
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { isUnauthorizedError } from "@/lib/authUtils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import CreateServiceTypeModal from "@/components/modals/create-service-type-modal";
import EditServiceTypeModal from "@/components/modals/edit-service-type-modal";
import { CreateServiceRateModal } from "@/components/modals/create-service-rate-modal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  FileText, 
  Building2, 
  Truck, 
  Search, 
  Filter,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Plus,
  Edit,
  Trash2,
  AlertTriangle,
  DollarSign
} from "lucide-react";

// Service type icon mapping
const getServiceTypeIcon = (category: string) => {
  switch (category) {
    case "Authority":
      return <FileText className="w-5 h-5" />;
    case "DOT":
      return <Truck className="w-5 h-5" />;
    case "Business Formation":
      return <Building2 className="w-5 h-5" />;
    case "Closure":
      return <XCircle className="w-5 h-5" />;
    case "Compliance":
      return <CheckCircle className="w-5 h-5" />;
    default:
      return <FileText className="w-5 h-5" />;
  }
};

// Service type color mapping
const getServiceTypeColor = (category: string) => {
  switch (category) {
    case "Authority":
      return "bg-blue-100 text-blue-800";
    case "DOT":
      return "bg-green-100 text-green-800";
    case "Business Formation":
      return "bg-yellow-100 text-yellow-800";
    case "Closure":
      return "bg-red-100 text-red-800";
    case "Compliance":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

interface WorkOrder {
  id: number;
  workOrderNumber: string;
  serviceType: string;
  status: string;
  carrierName: string | null;
  companyName: string | null;
  carrierDotNumber: string | null;
  carrierDocketNumber: string | null;
  carrierDocketPrefix: string | null;
  createdAt: string;
}

interface ServiceType {
  id: string;
  name: string;
  description: string;
  category: string;
  estimatedDays: string;
  relatedServiceTypes: string[];
  credentialTypeIds: number[];
  isActive: boolean;
}

interface CredentialType {
  id: number;
  name: string;
  agency: string | null;
  isActive: boolean;
}

export default function ServiceTypes() {
  usePageTitle("Service Types");
  const { toast } = useToast();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [selectedServiceType, setSelectedServiceType] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState<any>(null);
  // Rate guardrail state
  const [showAddRatePrompt, setShowAddRatePrompt] = useState(false);
  const [promptServiceType, setPromptServiceType] = useState<ServiceType | null>(null);
  const [showCreateRateModal, setShowCreateRateModal] = useState(false);
  const [createRateForServiceTypeId, setCreateRateForServiceTypeId] = useState<string | undefined>(undefined);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);

  const { data: workOrders = [], isLoading: workOrdersLoading } = useQuery<WorkOrder[]>({
    queryKey: ["/api/work-orders/list"],
    retry: false,
    refetchOnMount: "always",
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const { data: serviceTypes = [], isLoading: serviceTypesLoading } = useQuery<ServiceType[]>({
    queryKey: ["/api/service-types"],
    retry: false,
    refetchOnMount: "always",
  });

  const { data: credentialTypes = [] } = useQuery<CredentialType[]>({
    queryKey: ["/api/credential-types"],
    retry: false,
    refetchOnMount: "always",
  });

  const { data: serviceRates = [] } = useQuery<any[]>({
    queryKey: ["/api/service-rates"],
    retry: false,
    refetchOnMount: "always",
  });

  // Build a Set of service type IDs that have an effective (active, non-future) rate today
  const today = new Date().toISOString().split('T')[0];
  const serviceTypesWithActiveRate = new Set<string>(
    serviceRates
      .filter((r: any) => r.isActive && !r.deletedAt && r.effectiveDate <= today)
      .map((r: any) => r.serviceTypeId)
  );

  // Show loading state while authenticating
  if (isLoading || !isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const filteredServiceTypes = serviceTypes.filter((service: any) => {
    const matchesSearch = service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || service.category === selectedCategory;
    const matchesStatus = selectedStatus === "all" || 
                         (selectedStatus === "active" && service.isActive !== false) ||
                         (selectedStatus === "inactive" && service.isActive === false);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Mutations for CRUD operations
  const createServiceTypeMutation = useMutation({
    mutationFn: async (serviceTypeData: any) => {
      const res = await apiRequest("POST", "/api/service-types", serviceTypeData);
      return res.json();
    },
    onSuccess: (createdServiceType: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/service-types"] });
      queryClient.invalidateQueries({ queryKey: ["/api/service-rates"] });
      toast({
        title: "Success",
        description: "Service created successfully",
      });
      setShowCreateModal(false);
      // Prompt user to add a rate for the new service type (T004)
      if (createdServiceType?.id) {
        setPromptServiceType(createdServiceType);
        setShowAddRatePrompt(true);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create service",
        variant: "destructive",
      });
    },
  });

  const updateServiceTypeMutation = useMutation({
    mutationFn: async (serviceTypeData: any) => {
      await apiRequest("PUT", `/api/service-types/${serviceTypeData.id}`, serviceTypeData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/service-types"] });
      toast({
        title: "Success",
        description: "Service updated successfully",
      });
      setShowEditModal(false);
      setEditingServiceType(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update service",
        variant: "destructive",
      });
    },
  });

  const deleteServiceTypeMutation = useMutation({
    mutationFn: async (serviceTypeId: string) => {
      await apiRequest("DELETE", `/api/service-types/${serviceTypeId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/service-types"] });
      toast({
        title: "Success",
        description: "Service deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete service",
        variant: "destructive",
      });
    },
  });

  const getServiceTypeStats = (serviceTypeId: string) => {
    const orders = workOrders.filter(order => order.serviceType === serviceTypeId);
    const total = orders.length;
    const pending = orders.filter(order => order.status === "pending").length;
    const inProgress = orders.filter(order => order.status === "in_progress").length;
    const completed = orders.filter(order => order.status === "completed").length;
    
    return { total, pending, inProgress, completed };
  };

  const handleEditServiceType = (serviceType: any) => {
    setEditingServiceType(serviceType);
    setShowEditModal(true);
  };

  const handleDeleteServiceType = (serviceTypeId: string) => {
    if (window.confirm("Are you sure you want to delete this service? This action cannot be undone.")) {
      deleteServiceTypeMutation.mutate(serviceTypeId);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4" />;
      case "in_progress":
        return <Clock className="w-4 h-4" />;
      case "pending":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const categories = ["all", "Authority", "DOT", "Business Formation", "Closure"];

  return (
    <ResponsiveLayout title="Services" subtitle="Comprehensive overview of all services and their associations">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-end items-center">
          {user?.role === 'admin' && (
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Service
            </Button>
          )}
        </div>

        {/* Search and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-full sm:w-40">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem key={category} value={category}>
                {category === "all" ? "All Categories" : category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {serviceTypesLoading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading services...</p>
        </div>
      )}

      {/* Service Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredServiceTypes.map((serviceType: any) => {
          const stats = getServiceTypeStats(serviceType.id);
          const missingRate = serviceType.isActive && !serviceTypesWithActiveRate.has(serviceType.id);
          
          return (
            <Card key={serviceType.id} className="hover:shadow-lg transition-shadow h-full flex flex-col">
              <CardHeader className="pb-3 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                  {/* No-rate warning badge (T003) */}
                  {missingRate ? (
                    <div className="flex items-center gap-1 text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 text-xs font-medium">
                      <AlertTriangle className="h-3 w-3" />
                      No rate in effect
                    </div>
                  ) : (
                    <div />
                  )}
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedServiceType(serviceType.id)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {user?.role === 'admin' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditServiceType(serviceType)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                    )}
                    {user?.role === 'admin' && missingRate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        onClick={() => {
                          setCreateRateForServiceTypeId(serviceType.id);
                          setShowCreateRateModal(true);
                        }}
                        title="Add rate for this service"
                      >
                        <DollarSign className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${getServiceTypeColor(serviceType.category)}`}>
                    {getServiceTypeIcon(serviceType.category)}
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {serviceType.category}
                  </Badge>
                </div>
                <CardTitle className="text-lg leading-tight mb-1">{serviceType.name}</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <div className="flex-1">
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">{serviceType.description}</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Estimated Time:</span>
                      <span className="font-medium">{serviceType.estimatedDays}</span>
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-500">Work Orders</span>
                    <span className="font-medium">{stats.total} total</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1 text-xs text-center">
                    <div>
                      <div className="font-medium text-yellow-600">{stats.pending}</div>
                      <div className="text-gray-500">Pending</div>
                    </div>
                    <div>
                      <div className="font-medium text-blue-600">{stats.inProgress}</div>
                      <div className="text-gray-500">Progress</div>
                    </div>
                    <div>
                      <div className="font-medium text-green-600">{stats.completed}</div>
                      <div className="text-gray-500">Done</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Service Type Details Modal */}
      {selectedServiceType && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getServiceTypeColor(serviceTypes.find((s) => s.id === selectedServiceType)?.category || "")}`}>
                    {getServiceTypeIcon(serviceTypes.find((s) => s.id === selectedServiceType)?.category || "")}
                  </div>
                  <div>
                    <CardTitle className="text-xl">
                      {serviceTypes.find((s: any) => s.id === selectedServiceType)?.name}
                    </CardTitle>
                    <p className="text-gray-600 mt-1">
                      {serviceTypes.find((s: any) => s.id === selectedServiceType)?.description}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setSelectedServiceType(null)}
                >
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {(() => {
                const serviceType = serviceTypes.find((s: any) => s.id === selectedServiceType);
                const relatedOrders = workOrders.filter(order => order.serviceType === selectedServiceType);
                
                return (
                  <>
                    {/* Service Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Service Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div>
                            <span className="font-medium">Category:</span>
                            <Badge variant="outline" className="ml-2">
                              {serviceType?.category}
                            </Badge>
                          </div>
                          <div>
                            <span className="font-medium">Estimated time:</span>
                            <span className="ml-2 text-gray-600">{serviceType?.estimatedDays}</span>
                          </div>
                        </CardContent>
                      </Card>

                    </div>

                    {/* Related Work Orders */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Related Work Orders ({relatedOrders.length})</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {relatedOrders.length > 0 ? (
                          <div className="space-y-4">
                            {relatedOrders.map((order) => (
                              <div key={order.id} className="border rounded-lg p-4 hover:bg-gray-50">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                      <FileText className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                      <div className="font-medium">{order.workOrderNumber}</div>
                                      <div className="text-sm text-gray-600">
                                        {order.carrierName || "No carrier"} - {order.companyName || "N/A"}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <Badge className={getStatusColor(order.status)}>
                                      <div className="flex items-center gap-1">
                                        {getStatusIcon(order.status)}
                                        {order.status.replace('_', ' ')}
                                      </div>
                                    </Badge>
                                  </div>
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="font-medium">DOT Number:</span>
                                    <span className="ml-2 text-gray-600">{order.carrierDotNumber || "Not set"}</span>
                                  </div>
                                  <div>
                                    <span className="font-medium">Docket Number:</span>
                                    <span className="ml-2 text-gray-600">{order.carrierDocketNumber ? `${order.carrierDocketPrefix}-${order.carrierDocketNumber}` : "Not set"}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-8">
                            No work orders found for this service
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Empty State */}
      {!serviceTypesLoading && filteredServiceTypes.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No services found</h3>
          <p className="text-gray-500">
            {searchQuery || selectedCategory !== "all" 
              ? "Try adjusting your search or filters"
              : "Get started by adding your first service"
            }
          </p>
        </div>
      )}

      {/* Create Service Type Modal */}
      <CreateServiceTypeModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={createServiceTypeMutation.mutate}
        isLoading={createServiceTypeMutation.isPending}
        serviceTypes={serviceTypes}
        credentialTypes={credentialTypes}
      />

      {/* Edit Service Type Modal */}
      <EditServiceTypeModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingServiceType(null);
        }}
        onSubmit={updateServiceTypeMutation.mutate}
        isLoading={updateServiceTypeMutation.isPending}
        serviceType={editingServiceType}
        serviceTypes={serviceTypes}
        credentialTypes={credentialTypes}
      />

      {/* T004: Prompt to add rate after creating a new service type */}
      <AlertDialog open={showAddRatePrompt} onOpenChange={setShowAddRatePrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add a rate for "{promptServiceType?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This service has no rate yet. Work orders cannot be created until a rate is in effect.
              Would you like to add one now?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowAddRatePrompt(false);
                setPromptServiceType(null);
              }}
            >
              Do Later
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowAddRatePrompt(false);
                setCreateRateForServiceTypeId(promptServiceType?.id);
                setShowCreateRateModal(true);
                setPromptServiceType(null);
              }}
            >
              Add Rate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* T003 + T004: Create rate modal (used from card "Add Rate" button and post-creation prompt) */}
      <CreateServiceRateModal
        open={showCreateRateModal}
        onClose={() => {
          setShowCreateRateModal(false);
          setCreateRateForServiceTypeId(undefined);
        }}
        defaultServiceTypeId={createRateForServiceTypeId}
      />
      </div>
    </ResponsiveLayout>
  );
}